import { Injectable } from '@angular/core';
import {
  ForumAuthor,
  ForumCommunity,
  ForumPost,
  ForumSort,
  VoteValue,
} from './forum.types';

/**
 * Service de dados MOCKADOS do fórum (protótipo para apresentação).
 * Mantém o estado em memória durante a sessão da SPA: votos e novos posts
 * persistem enquanto o usuário navega entre as telas.
 * NÃO faz nenhuma chamada HTTP.
 */
@Injectable({ providedIn: 'root' })
export class ForumMockService {
  // ─────────────────────────────────────────────
  // Autores fake
  // ─────────────────────────────────────────────
  private readonly authors: Record<string, ForumAuthor> = {
    diego: { id: 'u1', name: 'Diego Arrancada', initials: 'DA', avatarColor: '#AC281C', flair: 'FT600 · Etanol', isTunner: false },
    bruna: { id: 'u2', name: 'Bruna Mapas', initials: 'BM', avatarColor: '#1d6fb8', flair: 'Tunner Verificado', isTunner: true },
    rafa: { id: 'u3', name: 'Rafael Boost', initials: 'RB', avatarColor: '#2e8b57', flair: 'AP Turbo' },
    leo: { id: 'u4', name: 'Léo Datalog', initials: 'LD', avatarColor: '#b8862f', flair: 'FT550' },
    carla: { id: 'u5', name: 'Carla Setup', initials: 'CS', avatarColor: '#7d3cad', flair: 'Tunner Verificado', isTunner: true },
    juca: { id: 'u6', name: 'Juca Garagem', initials: 'JG', avatarColor: '#c0563b', flair: 'Novato' },
    ftcrew: { id: 'u7', name: 'Equipe FuelTech', initials: 'FT', avatarColor: '#AC281C', flair: 'Oficial', isTunner: true },
    marcao: { id: 'u8', name: 'Marcão V8', initials: 'MV', avatarColor: '#444b55', flair: 'Opala 6cc' },
  };

  // ─────────────────────────────────────────────
  // Comunidades fake
  // ─────────────────────────────────────────────
  private readonly communities: ForumCommunity[] = [
    { id: 'c1', slug: 'preparacao-motor', name: 'Preparação & Motor', description: 'Montagem, forjados, comando, cabeçote e tudo que faz girar.', icon: 'pi pi-bolt', color: '#AC281C', members: 18420, online: 312 },
    { id: 'c2', slug: 'duvidas-tecnicas', name: 'Dúvidas Técnicas', description: 'Tá travado no acerto? Pergunte aqui — a comunidade responde.', icon: 'pi pi-question-circle', color: '#1d6fb8', members: 24180, online: 540 },
    { id: 'c3', slug: 'datalogger-acerto', name: 'Datalogger & Acerto', description: 'Leitura de log, malha fechada, knock e estratégia de mapa.', icon: 'pi pi-chart-line', color: '#2e8b57', members: 9320, online: 121 },
    { id: 'c4', slug: 'injecao-ft', name: 'Injeção FT', description: 'FT450/500/550/600/650 — dúvidas, recursos e firmware.', icon: 'pi pi-microchip', color: '#b8862f', members: 15730, online: 268 },
    { id: 'c5', slug: 'turbo-aspiracao', name: 'Turbo & Aspiração', description: 'Dimensionamento de turbina, A/R, antilag e wastegate.', icon: 'pi pi-sync', color: '#7d3cad', members: 11210, online: 173 },
    { id: 'c6', slug: 'meu-projeto', name: 'Apresente seu Projeto', description: 'Mostre seu carro, sua build e a evolução da garagem.', icon: 'pi pi-car', color: '#c0563b', members: 20940, online: 401 },
  ];

  // ─────────────────────────────────────────────
  // Posts fake (com comentários)
  // ─────────────────────────────────────────────
  private posts: ForumPost[] = [
    {
      id: 'p1',
      communityId: 'c4',
      author: this.authors['rafa'],
      title: 'FT550 ou FT600 pra um AP turbo de arrancada? Vale o upgrade?',
      body: 'Tô montando um AP 2.0 8v turbo focado em arrancada (até 1.8 bar, etanol). Já tenho uma FT550 sobrando, mas tô na dúvida se compenso partir direto pra FT600 por causa dos canais extras e do controle de tração. Pra esse uso, a 550 entrega tudo que preciso ou vou me arrepender lá na frente?',
      tag: 'Dúvida', tagColor: '#1d6fb8',
      score: 247, myVote: 0, commentsCount: 38, createdAt: 'há 5 h', hotRank: 980, saved: false, image: null,
      comments: [
        {
          id: 'p1c1', author: this.authors['bruna'], body: 'Pra arrancada pura a 550 dá conta tranquilo até onde você falou. O pulo pra 600 só compensa se você quiser controle de largada (launch), 2-step refinado e mais saídas pra solenoides. Se é grana curta, fica na 550 e investe no resto da build.', score: 142, myVote: 0, createdAt: 'há 4 h', isOp: false,
          replies: [
            { id: 'p1c1r1', author: this.authors['rafa'], body: 'Boa! Launch control eu vou querer sim... talvez valha a 600 então.', score: 31, myVote: 0, createdAt: 'há 3 h', isOp: true },
            { id: 'p1c1r2', author: this.authors['carla'], body: 'Se launch é prioridade, vai de 600 sem dó. Faz diferença gigante na consistência de tempo.', score: 54, myVote: 0, createdAt: 'há 3 h' },
          ],
        },
        { id: 'p1c2', author: this.authors['marcao'], body: 'Tenho 550 no meu e nunca senti falta pra arrancada. Mas concordo: launch control da 600 é vício.', score: 67, myVote: 0, createdAt: 'há 4 h' },
        { id: 'p1c3', author: this.authors['leo'], body: 'Lembra que a 600 já vem com entrada pra sonda wideband sem módulo externo. Isso por si só já me convenceu na minha.', score: 45, myVote: 0, createdAt: 'há 2 h' },
      ],
    },
    {
      id: 'p2',
      communityId: 'c2',
      author: this.authors['juca'],
      title: 'Golf GTI deu detonação a 1.2 bar no etanol — por onde começo a investigar?',
      body: 'Galera, meu Golf MK7 GTI com Stage 2 começou a acusar detonação leve no datalogger acima de 1.2 bar, só no etanol. Vela está nova (um grau mais fria), bicos originais. A pressão de combustível cai um pouco no fim do giro. Pode ser bomba? Ou já é hora de bico maior?',
      tag: 'Dúvida', tagColor: '#1d6fb8',
      score: 189, myVote: 1, commentsCount: 27, createdAt: 'há 8 h', hotRank: 870, saved: true, image: null,
      comments: [
        { id: 'p2c1', author: this.authors['bruna'], body: 'Queda de pressão no fim do giro + detonação = clássico de bomba/bico no limite. Mede a pressão de combustível com manômetro inline antes de culpar o mapa. Se cai mais de 0.3 bar sob carga, achou o gargalo.', score: 98, myVote: 0, createdAt: 'há 7 h',
          replies: [
            { id: 'p2c1r1', author: this.authors['juca'], body: 'Vou medir hoje à noite e volto aqui com o log. Valeu!', score: 12, myVote: 0, createdAt: 'há 6 h', isOp: true },
          ],
        },
        { id: 'p2c2', author: this.authors['leo'], body: 'Posta o log se puder. Detonação "leve" às vezes é ruído de válvula mal interpretado pelo knock. Importante separar uma coisa da outra antes de mexer no avanço.', score: 61, myVote: 0, createdAt: 'há 6 h' },
      ],
    },
    {
      id: 'p3',
      communityId: 'c3',
      author: this.authors['bruna'],
      title: '[GUIA] Como ler malha fechada no datalogger da FT sem se enganar com o lambda',
      body: 'Escrevi um passo a passo de como interpretar a correção de malha fechada nos logs da FT, porque vejo MUITA gente acertando o mapa olhando o lambda no transiente (errado!). Cobre: o que é correção integral vs proporcional, quando ignorar o pico de aceleração, e como usar a tabela de target. Print dos canais que importam no fim.',
      tag: 'Guia', tagColor: '#2e8b57',
      score: 512, myVote: 0, commentsCount: 64, createdAt: 'há 1 d', hotRank: 1450, saved: false, image: 'datalog',
      comments: [
        { id: 'p3c1', author: this.authors['ftcrew'], body: 'Conteúdo excelente, fixamos aqui na comunidade. 👏', score: 120, myVote: 0, createdAt: 'há 22 h' },
        { id: 'p3c2', author: this.authors['rafa'], body: 'Cara, eu acertava exatamente errado — olhando lambda no transiente. Isso explica meu carro engasgando. Salvando agora.', score: 88, myVote: 0, createdAt: 'há 20 h' },
        { id: 'p3c3', author: this.authors['marcao'], body: 'Faz um sobre knock detection também! Tem muita lenda sobre o canal de knock da FT.', score: 53, myVote: 0, createdAt: 'há 18 h' },
      ],
    },
    {
      id: 'p4',
      communityId: 'c6',
      author: this.authors['diego'],
      title: 'Apresentando meu Civic EG turbo — 480cv no etanol, build de 3 anos 🔧',
      body: 'Depois de 3 anos (e muito boleto) finalmente bati 480cv na roda com meu EG. D16 com kit forjado, turbina .63, FT600, bicos 1000cc, comando especial. Tudo acertado em casa com a ajuda dessa comunidade. Obrigado a cada um que respondeu minhas dúvidas de novato lá em 2023!',
      tag: 'Build', tagColor: '#c0563b',
      score: 873, myVote: 1, commentsCount: 96, createdAt: 'há 12 h', hotRank: 1620, saved: true, image: 'civic',
      comments: [
        { id: 'p4c1', author: this.authors['carla'], body: 'Que projeto lindo! O acabamento do cofre tá impecável. Manda o setup de comando que ficou curioso.', score: 134, myVote: 0, createdAt: 'há 11 h' },
        { id: 'p4c2', author: this.authors['juca'], body: 'Isso que é inspiração. Tô começando minha build agora e ver esse resultado me deu gás. 🔥', score: 76, myVote: 0, createdAt: 'há 10 h' },
      ],
    },
    {
      id: 'p5',
      communityId: 'c5',
      author: this.authors['leo'],
      title: 'Bicos de 1000cc são suficientes pra 500cv em E85? Fiz a conta e fiquei na dúvida',
      body: 'Fiz o cálculo de vazão (BSFC ~0.55 pro E85) e deu meio no limite pros 500cv que quero. Dá uns 85% de duty cycle no pico. Tá apertado demais ou é aceitável? Não quero ficar trocando bico daqui a 6 meses quando subir mais pressão.',
      tag: 'Dúvida', tagColor: '#1d6fb8',
      score: 156, myVote: 0, commentsCount: 19, createdAt: 'há 3 h', hotRank: 760, saved: false, image: null,
      comments: [
        { id: 'p5c1', author: this.authors['bruna'], body: '85% de duty já no alvo atual é apertado pra quem pretende subir. Regra de bolso: projete pra ficar abaixo de 80% no pico do que você QUER chegar, não do que tem hoje. Eu iria de 1300cc.', score: 72, myVote: 0, createdAt: 'há 2 h' },
        { id: 'p5c2', author: this.authors['diego'], body: 'Passei exatamente por isso. Fui de 1000cc, durou um verão. Troquei pra 1300 e parei de me preocupar. Compra uma vez e chora uma vez.', score: 49, myVote: 0, createdAt: 'há 2 h' },
      ],
    },
    {
      id: 'p6',
      communityId: 'c4',
      author: this.authors['ftcrew'],
      title: '📢 Firmware FT 5.21 liberado — changelog e o que mudou no controle de boost',
      body: 'Saiu a versão 5.21 do firmware. Destaques: novo algoritmo de controle de boost em malha fechada (bem mais estável em altitude), correção no log de knock e suporte ampliado a sensores flex. Atualizem pelo FTManager. Dúvidas, respondemos aqui no tópico.',
      tag: 'Oficial', tagColor: '#AC281C',
      score: 421, myVote: 0, commentsCount: 73, createdAt: 'há 1 d', hotRank: 1390, saved: false, pinned: true, image: null,
      comments: [
        { id: 'p6c1', author: this.authors['rafa'], body: 'O controle de boost em altitude melhorou MESMO. Subi a serra e o boost não oscilou nada. 👏', score: 64, myVote: 0, createdAt: 'há 20 h' },
        { id: 'p6c2', author: this.authors['leo'], body: 'Atualizei e o log de knock ficou bem mais limpo. Recomendo geral.', score: 41, myVote: 0, createdAt: 'há 19 h' },
      ],
    },
    {
      id: 'p7',
      communityId: 'c1',
      author: this.authors['marcao'],
      title: 'Comando 280° no Opala 6cc deixou marcha lenta horrível — normal ou errei algo?',
      body: 'Botei um comando 280°/280° no meu Opala 6 cilindros e a marcha lenta ficou MUITO instável, morre se eu não pisar. É a característica do comando bravo mesmo ou tem como domar isso na FT com estratégia de lenta? Tô na FT450.',
      tag: 'Dúvida', tagColor: '#1d6fb8',
      score: 98, myVote: 0, commentsCount: 22, createdAt: 'há 6 h', hotRank: 640, saved: false, image: null,
      comments: [
        { id: 'p7c1', author: this.authors['carla'], body: 'Comando bravo + lenta baixa = briga eterna. Na FT você ameniza com controle de lenta por PWM e um avanço de lenta mais agressivo. Mas aceita: 280° não foi feito pra lenta de táxi. 😅', score: 55, myVote: 0, createdAt: 'há 5 h' },
        { id: 'p7c2', author: this.authors['bruna'], body: 'Sobe a lenta alvo pra ~950-1000 e configura o avanço de lenta pra "segurar" a rotação. Melhora MUITO. Manda teu arquivo que dou uma olhada.', score: 47, myVote: 0, createdAt: 'há 4 h' },
      ],
    },
    {
      id: 'p8',
      communityId: 'c3',
      author: this.authors['diego'],
      title: 'Partida a frio no flex tá uma novela — alguém tem uma boa estratégia de injeção?',
      body: 'No frio (abaixo de 15°C) meu carro flex custa MUITO pra pegar no etanol e morre algumas vezes antes de estabilizar. Já mexi no enriquecimento de partida e no afogador eletrônico mas não tô feliz. Como vocês configuram a partida a frio com etanol na FT?',
      tag: 'Dúvida', tagColor: '#1d6fb8',
      score: 134, myVote: 0, commentsCount: 31, createdAt: 'há 10 h', hotRank: 700, saved: false, image: null,
      comments: [
        { id: 'p8c1', author: this.authors['bruna'], body: 'A chave é a tabela de enriquecimento por temperatura do motor + tempo de injeção de partida generoso nos primeiros ciclos. E não esquece do "after start enrichment" decaindo suave. Etanol no frio precisa de muito mais combustível do que a galera imagina.', score: 81, myVote: 0, createdAt: 'há 9 h' },
      ],
    },
  ];

  private nextId = 100;

  // ─────────────────────────────────────────────
  // Leitura
  // ─────────────────────────────────────────────
  getCommunities(): ForumCommunity[] {
    return this.communities;
  }

  getCommunityBySlug(slug: string): ForumCommunity | undefined {
    return this.communities.find((c) => c.slug === slug);
  }

  getCommunityById(id: string): ForumCommunity | undefined {
    return this.communities.find((c) => c.id === id);
  }

  /** Posts ordenados, opcionalmente filtrados por comunidade. */
  getPosts(sort: ForumSort = 'hot', communityId?: string): ForumPost[] {
    let list = [...this.posts];
    if (communityId) {
      list = list.filter((p) => p.communityId === communityId);
    }
    // Posts fixados sempre no topo
    const pinned = list.filter((p) => p.pinned);
    const rest = list.filter((p) => !p.pinned);
    rest.sort((a, b) => {
      if (sort === 'new') return b.id.localeCompare(a.id) || 0;
      if (sort === 'top') return b.score - a.score;
      return b.hotRank - a.hotRank; // hot
    });
    return [...pinned, ...rest];
  }

  getPostById(id: string): ForumPost | undefined {
    return this.posts.find((p) => p.id === id);
  }

  /** Total de membros somados (para stats de cabeçalho). */
  getTotalMembers(): number {
    return this.communities.reduce((acc, c) => acc + c.members, 0);
  }

  // ─────────────────────────────────────────────
  // Mutação (fake, em memória)
  // ─────────────────────────────────────────────
  /** Aplica voto a um post e ajusta o score relativo ao voto anterior. */
  votePost(post: ForumPost, value: VoteValue): void {
    post.score += value - post.myVote;
    post.myVote = post.myVote === value ? 0 : value;
    // Corrige score quando o voto é "desfeito"
    if (post.myVote === 0) {
      post.score -= value;
    }
  }

  /** Voto em comentário (mesma lógica). */
  voteComment(comment: { score: number; myVote: VoteValue }, value: VoteValue): void {
    if (comment.myVote === value) {
      comment.score -= value;
      comment.myVote = 0;
    } else {
      comment.score += value - comment.myVote;
      comment.myVote = value;
    }
  }

  toggleSave(post: ForumPost): void {
    post.saved = !post.saved;
  }

  /** Cria um post fake e coloca no topo do feed. */
  createPost(input: {
    communityId: string;
    title: string;
    body: string;
    tag?: string;
  }): ForumPost {
    const community = this.getCommunityById(input.communityId) ?? this.communities[0];
    const post: ForumPost = {
      id: `p${this.nextId++}`,
      communityId: community.id,
      author: { id: 'me', name: 'Você', initials: 'VC', avatarColor: '#AC281C', flair: 'Você' },
      title: input.title.trim(),
      body: input.body.trim(),
      tag: input.tag || 'Discussão',
      tagColor: input.tag === 'Build' ? '#c0563b' : input.tag === 'Guia' ? '#2e8b57' : '#1d6fb8',
      score: 1,
      myVote: 1,
      commentsCount: 0,
      createdAt: 'agora',
      hotRank: 2000, // entra no topo de "Em alta"
      saved: false,
      image: null,
      comments: [],
    };
    this.posts = [post, ...this.posts];
    return post;
  }

  /** Adiciona comentário fake na raiz do post. */
  addComment(post: ForumPost, body: string): void {
    post.comments = [
      {
        id: `${post.id}c${this.nextId++}`,
        author: { id: 'me', name: 'Você', initials: 'VC', avatarColor: '#AC281C', flair: 'Você' },
        body: body.trim(),
        score: 1,
        myVote: 1,
        createdAt: 'agora',
        isOp: false,
      },
      ...post.comments,
    ];
    post.commentsCount += 1;
  }
}
